/**
 * comm-assist-api.ts — tolerant, feature-detected client for the Kommunikation
 * KI-Assistenz block (Story UI-U9).
 *
 * The endpoint `GET /communications/:commId/assist` (summary / draft / confidence)
 * is OWNED by the parallel backend-gaps wave and is ASSUMED — its final path/shape
 * may differ. This module therefore:
 *   - accepts key ALIASES + an ENVELOPE wrapper,
 *   - coerces summary/draft to non-empty strings only,
 *   - normalizes confidence from fraction / scaled number / percent-string,
 *   - HIDES on anything unrecognized (both fields empty → null),
 *   - swallows ANY fetch/HTTP error to null (404 = endpoint absent).
 * Result: whatever the wave finally ships degrades to "block absent", never a crash.
 *
 * [Source: apps/web/src/lib/api-client.ts#apiFetch] (throws on !response.ok; 204 → undefined)
 * [Source: docs/stories/ui-u9-kommunikation-assistenz.md AC1/AC2]
 */

import { apiFetch } from '../../../lib/api-client';

/** The normalized assist view-model. `null` fields → the block hides that part. */
export interface CommAssist {
  summary: string | null;
  draft: string | null;
  /** 0–100, or null when the payload carries no usable confidence. */
  confidence: number | null;
}

/** Anything mail-shaped enough to derive a mock assist from. */
export interface MockAssistSource {
  kiZusammenfassung?: string | null;
  kiAntwort?: string | null;
  konfidenz?: number | null;
}

const SUMMARY_KEYS = [
  'summary',
  'zusammenfassung',
  'kiZusammenfassung',
  'ki_zusammenfassung',
] as const;
const DRAFT_KEYS = [
  'draft',
  'reply',
  // The api CommAssistDto ships the draft as `reply_draft` (GET + POST refresh).
  'reply_draft',
  'replyDraft',
  'antwort',
  'antwortvorschlag',
  'kiAntwort',
  'ki_antwort',
] as const;
const CONFIDENCE_KEYS = ['confidence', 'konfidenz', 'score'] as const;
const ENVELOPE_KEYS = ['data', 'assist', 'result'] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** First non-null value among the alias keys, else undefined. */
function pick(obj: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const k of keys) {
    const v = obj[k];
    if (v !== null && v !== undefined) return v;
  }
  return undefined;
}

/** Non-empty trimmed string, else null. */
function asString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

/**
 * Fraction (0..1) → ×100; already-scaled (>1..100) → rounded; string ("88",
 * "88%", "0.88") → parsed then the same rule. Non-finite / negative /
 * unparseable → null. Clamped to ≤100.
 */
export function normalizeConfidence(v: unknown): number | null {
  let n: number;
  if (typeof v === 'number') {
    n = v;
  } else if (typeof v === 'string') {
    const cleaned = v.replace('%', '').trim();
    if (cleaned === '') return null;
    n = Number(cleaned);
  } else {
    return null;
  }
  if (!Number.isFinite(n) || n < 0) return null;
  const scaled = n <= 1 ? n * 100 : n;
  return Math.min(100, Math.round(scaled));
}

/**
 * Parse the assumed assist payload into a CommAssist, or null when there is
 * nothing to show / the shape is unrecognized.
 */
export function parseCommAssist(raw: unknown): CommAssist | null {
  if (!isPlainObject(raw)) return null;

  // Envelope unwrap: only descend when the top object carries no known content key.
  let obj = raw;
  const topHasContent = [...SUMMARY_KEYS, ...DRAFT_KEYS].some((k) => obj[k] != null);
  if (!topHasContent) {
    for (const key of ENVELOPE_KEYS) {
      const inner = obj[key];
      if (isPlainObject(inner)) {
        obj = inner;
        break;
      }
    }
  }

  const summary = asString(pick(obj, SUMMARY_KEYS));
  const draft = asString(pick(obj, DRAFT_KEYS));
  if (summary === null && draft === null) return null;

  return { summary, draft, confidence: normalizeConfidence(pick(obj, CONFIDENCE_KEYS)) };
}

/**
 * Feature-detect + fetch the assist for one communication. Returns null on ANY
 * failure (absent endpoint → apiFetch throws on 404; 204 → undefined → null).
 */
export async function fetchCommAssist(commId: string): Promise<CommAssist | null> {
  try {
    const raw = await apiFetch<unknown>(`/communications/${commId}/assist`);
    return parseCommAssist(raw);
  } catch {
    return null;
  }
}

/**
 * Force-regenerate the assist for one communication (POST
 * /communications/:commId/assist/refresh — always a fresh model call). Mirrors
 * fetchCommAssist's tolerant parse; returns null on ANY failure / unrecognized shape.
 * Powers the "KI-Vorschlag generieren" action in the reply composer.
 */
export async function refreshCommAssist(commId: string): Promise<CommAssist | null> {
  try {
    const raw = await apiFetch<unknown>(`/communications/${commId}/assist/refresh`, {
      method: 'POST',
    });
    return parseCommAssist(raw);
  } catch {
    return null;
  }
}

/** Mock-mode assist: derive from the mail's KI fields (today's behaviour). */
export function deriveMockAssist(mail: MockAssistSource | null): CommAssist | null {
  if (!mail) return null;
  const summary = asString(mail.kiZusammenfassung);
  const draft = asString(mail.kiAntwort);
  if (summary === null && draft === null) return null;
  return { summary, draft, confidence: normalizeConfidence(mail.konfidenz) };
}

/** Badge value: the assist confidence, or the task konfidenz when absent. */
export function resolveAssistKonfidenz(assist: CommAssist | null, taskKonfidenz: number): number {
  return assist?.confidence ?? taskKonfidenz;
}
